/**
 * Where guides live: matching the current route to a screen, and the reader page's path.
 *
 * Plain functions, kept out of the component files so those export components only.
 */

import { matchPath } from 'react-router-dom';

import type { GuideScreen } from './types';

/** The query parameter a reader page's "Guide me" sets to open a walkthrough on a screen. */
export const GUIDE_PARAM = 'guide';

/**
 * Set alongside {@link GUIDE_PARAM} by Review & Edit's "Try it": the draft version to
 * preview. The server honours it only for authors, and only as a preview (G18).
 */
export const GUIDE_VERSION_PARAM = 'guideVersion';

/** The screen whose route pattern matches `pathname`; the longest pattern wins. */
export function resolveScreen(
  screens: GuideScreen[],
  pathname: string
): GuideScreen | null {
  let best: GuideScreen | null = null;
  for (const screen of screens) {
    if (!screen.route_pattern) continue;
    if (!matchPath({ path: screen.route_pattern, end: true }, pathname)) continue;
    if (!best || screen.route_pattern.length > best.route_pattern.length) best = screen;
  }
  return best;
}

/** The reader page for a walkthrough: its steps and screenshots, printable as a PDF. */
export function guideReaderPath(toolKey: string, topicKey: string): string {
  return `/guides/${encodeURIComponent(toolKey)}/${encodeURIComponent(topicKey)}`;
}

/** The Review & Edit page for one guide. */
export function guideReviewPath(toolKey: string, topicKey: string): string {
  return `/admin/guidance/${encodeURIComponent(toolKey)}/${encodeURIComponent(topicKey)}`;
}

/**
 * Where "Try it" goes: the guide's screen, opening the draft version in preview. Null
 * for a route with `:params`, which cannot be navigated to without values.
 */
export function guideTryItPath(
  routePattern: string,
  topicKey: string,
  version: number
): string | null {
  if (!routePattern || routePattern.includes(':')) return null;
  const params = new URLSearchParams({
    [GUIDE_PARAM]: topicKey,
    [GUIDE_VERSION_PARAM]: String(version),
  });
  return `${routePattern}?${params.toString()}`;
}
